/**
 * Sol menüde açılıp kapanabilen bölüm başlığı (Dashboard'lar, Favoriler, Modüller).
 *
 * Modül klasörleri zaten açılıp kapanıyordu; üstteki bölümler kapanmıyordu ve uzun
 * favori listesi modül ağacını ekranın dışına itiyordu. Durum oturum boyunca korunur
 * (localStorage): kullanıcı her sayfa yenilemesinde aynı bölümleri kapatmak zorunda
 * kalmasın.
 */
import { useCallback, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/shared/lib/cn';

const STORAGE_PREFIX = 'octo.web.nav.section.';

function readCollapsed(id: string, fallback: boolean): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + id);
    return raw === null ? fallback : raw === '1';
  } catch {
    // Gizli sekmede / depolama kapalıyken varsayılanla devam edilir.
    return fallback;
  }
}

export interface NavSectionProps {
  /** Depolama anahtarı — bölüm kimliği. */
  id: string;
  title: string;
  icon?: ReactNode;
  count?: number;
  defaultCollapsed?: boolean;
  children: ReactNode;
}

export function NavSection({ id, title, icon, count, defaultCollapsed = false, children }: NavSectionProps) {
  const [collapsed, setCollapsed] = useState(() => readCollapsed(id, defaultCollapsed));

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_PREFIX + id, next ? '1' : '0');
      } catch {
        /* depolama yoksa yalnızca bu oturumda geçerli */
      }
      return next;
    });
  }, [id]);

  return (
    <section className="mb-1">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={!collapsed}
        className="text-fg-subtle hover:bg-bg-hover hover:text-fg-muted flex w-full items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold tracking-wide uppercase"
      >
        {collapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        {icon}
        <span className="truncate">{title}</span>
        {typeof count === 'number' && count > 0 ? (
          <span className="ml-auto font-normal normal-case">{count}</span>
        ) : null}
      </button>
      <div className={cn(collapsed && 'hidden')}>{children}</div>
    </section>
  );
}
